import { Boundaries } from "@planzone/core";
import {
  Identifiable,
  list,
  List,
  OptimizedTimeSheet,
  optimizedTimeSheet,
  ProjectElementType,
  task,
  Task,
} from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { reduceResource } from "../../common/resource";
import { Selector, State } from "./types";
import { createSelector } from "./selector";
import { matchRequest } from "./utils";
import { reduceList } from "../../common/list";
import { reduceTask } from "../../common/task";

const initialState: State = [];

function handleRequest(
  state: State,
  {
    project,
    resource,
    page,
    size,
    filter,
  }: Boundaries["retrieveTimeSheet"]["request"],
): State {
  const index = state.findIndex((req) =>
    matchRequest(req, project, resource, filter, size),
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          resource,
          filter,
          page,
          size,
          canLoadMore: true,
          request: setRequestStateToPending(),
          timeSheet: optimizedTimeSheet,
          isDirty: false,
        },
      ];
}

function handleSuccess(
  state: State,
  {
    project,
    resource,
    page,
    size,
    filter,
    timeSheet,
  }: Boundaries["retrieveTimeSheet"]["success"],
): State {
  const index = state.findIndex((req) =>
    matchRequest(req, project, resource, filter, size),
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          timeSheet:
            page === 0 && !state[index].isDirty
              ? timeSheet
              : {
                  ...state[index].timeSheet,
                  elements: [
                    ...state[index].timeSheet.elements.slice(0, page * size),
                    ...timeSheet.elements,
                    ...state[index].timeSheet.elements.slice(
                      page * size + size,
                    ),
                  ],
                },
          canLoadMore: state[index].isDirty
            ? state[index].canLoadMore
            : timeSheet.elements.length >= size,
          page: state[index].page + 1,
          isDirty: false,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          resource,
          filter,
          page: state[index].page + 1,
          size,
          canLoadMore: timeSheet.elements.length >= size,
          request: setRequestStateToSuccess(),
          timeSheet,
          isDirty: false,
        },
      ];
}

function handleFailure(
  state: State,
  {
    project,
    resource,
    page,
    size,
    filter,
  }: Boundaries["retrieveTimeSheet"]["failure"],
): State {
  const index = state.findIndex((req) =>
    matchRequest(req, project, resource, filter, size),
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          resource,
          filter,
          page,
          size,
          canLoadMore: true,
          request: setRequestStateToFailure(),
          timeSheet: optimizedTimeSheet,
          isDirty: false,
        },
      ];
}

function handleAllocationResources(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleEditAllocation(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleRemoveAllocation(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleValidateTimeRecordsUpToDate(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleDisableProjectResource(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleActivateProjectResource(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleEditTimesheetResource(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleMarkTaskAsCompleted(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleRemoveResourceFromProject(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleRemoveResourceFromPlanzone(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function removeElementAndDescendants(
  elements: OptimizedTimeSheet["elements"],
  uuid: string,
): OptimizedTimeSheet["elements"] {
  const removedUuids = new Set([uuid]);
  let hasNewRemoval = true;

  while (hasNewRemoval) {
    hasNewRemoval = false;
    elements.forEach((element) => {
      if (
        !removedUuids.has(element.uuid) &&
        removedUuids.has(element.parentUuid)
      ) {
        removedUuids.add(element.uuid);
        hasNewRemoval = true;
      }
    });
  }

  return elements.filter((element) => !removedUuids.has(element.uuid));
}

function handleRemoveTask(state: State, task: Identifiable<Task>): State {
  return state.map((req) => ({
    ...req,
    timeSheet: {
      ...req.timeSheet,
      elements: removeElementAndDescendants(req.timeSheet.elements, task.uuid),
    },
  }));
}

function handleRemoveList(state: State, list: Identifiable<List>): State {
  return state.map((req) => ({
    ...req,
    timeSheet: {
      ...req.timeSheet,
      elements: removeElementAndDescendants(req.timeSheet.elements, list.uuid),
    },
  }));
}

function handleReload(
  state: State,
  {
    project,
    filter,
    timeSheet,
  }: Boundaries["reloadProjectTimeSheet"]["success"],
): State {
  // const index = state.findIndex((req) =>
  //   matchRequest(req, project, resource, filter, size),
  // );
  //
  // return index !== -1
  //   ? [
  //       ...state.slice(0, index),
  //       {
  //         ...state[index],
  //         timeSheet: mergeTS(state[index].timeSheet, timeSheet),
  //       },
  //       ...state.slice(index + 1),
  //     ]
  //   : [
  //       ...state,
  //       {
  //         project,
  //         filter,
  //         request: setRequestStateToSuccess(),
  //         timeSheet,
  //         isDirty: false,
  //       },
  //     ];
  return state;
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((req) => ({
    ...req,
    timeSheet: {
      ...req.timeSheet,
      resource: {
        ...req.timeSheet.resource,
        ...reduceResource(req.timeSheet.resource, event),
      },
      elements: req.timeSheet.elements.map((element) =>
        element.type === ProjectElementType.LIST
          ? {
              ...element,
              ...reduceList({ ...list, ...element }, event),
              type: ProjectElementType.LIST,
            }
          : {
              ...element,
              ...reduceTask({ ...task, ...element }, event),
              type: ProjectElementType.TASK,
            },
      ),
    },
  }));
}

const reduceState = createStateReducer(
  initialState,
  {
    [events.retrieveTimeSheetRequested]: handleRequest,
    [events.retrieveTimeSheetSucceeded]: handleSuccess,
    [events.retrieveTimeSheetFailed]: handleFailure,
    [events.allocateResourcesSucceeded]: handleAllocationResources,
    [events.editAllocationSucceeded]: handleEditAllocation,
    [events.removeAllocationSucceeded]: handleRemoveAllocation,
    [events.validateTimeRecordsUpToDateSucceeded]:
      handleValidateTimeRecordsUpToDate,
    [events.validatePersonalTimeRecordsUpToDateSucceeded]:
      handleValidateTimeRecordsUpToDate,
    [events.reloadProjectTimeSheetSucceeded]: handleReload,
    [events.disableProjectResourceSucceeded]: handleDisableProjectResource,
    [events.activateProjectResourceSucceeded]: handleActivateProjectResource,
    [events.removeResourceFromProjectSucceeded]:
      handleRemoveResourceFromProject,
    [events.removeResourceFromPlanzoneSucceeded]:
      handleRemoveResourceFromPlanzone,
    [events.saveTimeRecordSucceeded]: handleEditTimesheetResource,
    [events.deleteTimeRecordSucceeded]: handleEditTimesheetResource,
    [events.markTaskAsCompletedSucceeded]: handleMarkTaskAsCompleted,
    [events.removeTaskSucceeded]: handleRemoveTask,
    [events.removeListSucceeded]: handleRemoveList,
  },
  propagateEvent,
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
